<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  BUTTON_TYPES,
  MAX_TEMPLATE_BUTTONS,
  emptyTemplateButton,
} from 'dashboard/helper/whatsappTemplateButtons';

const props = defineProps({
  inboxId: { type: [Number, String], default: '' },
});
const buttons = defineModel({ type: Array, default: () => [] });

const flows = ref([]);
onMounted(async () => {
  try {
    const params = props.inboxId ? { inbox_id: props.inboxId } : {};
    const { data } = await WhatsappFlowsAPI.list(params);
    flows.value = data.payload || [];
  } catch {
    flows.value = [];
  }
});

const countOf = type => buttons.value.filter(item => item.type === type).length;
const available = type => {
  const config = BUTTON_TYPES.find(item => item.value === type);
  return countOf(type) < config.limit;
};
const optionsFor = button =>
  BUTTON_TYPES.filter(
    item => item.value === button.type || available(item.value)
  );
// Lista suspensa ancorada no botão: abre para cima quando não cabe abaixo.
const menuOpen = ref(false);
const menuUp = ref(false);
const menuRoot = ref(null);
const MENU_HEIGHT = 320;
const toggleMenu = async () => {
  menuOpen.value = !menuOpen.value;
  if (!menuOpen.value) return;
  const rect = menuRoot.value?.getBoundingClientRect();
  menuUp.value =
    !!rect &&
    window.innerHeight - rect.bottom < MENU_HEIGHT &&
    rect.top > MENU_HEIGHT;
  await nextTick();
};
onClickOutside(menuRoot, () => {
  menuOpen.value = false;
});
onKeyStroke('Escape', () => {
  menuOpen.value = false;
});
const add = type => {
  menuOpen.value = false;
  if (buttons.value.length >= MAX_TEMPLATE_BUTTONS || !available(type)) return;
  buttons.value = [...buttons.value, emptyTemplateButton(type)];
};
const remove = index => {
  buttons.value = buttons.value.filter((_, position) => position !== index);
};
const full = computed(() => buttons.value.length >= MAX_TEMPLATE_BUTTONS);
const selectFlow = (button, id) => {
  button.flow_id = id;
  const flow = flows.value.find(item => String(item.meta_flow_id) === id);
  if (flow?.first_screen_id) button.navigate_screen = flow.first_screen_id;
};
const INPUT =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="grid gap-3">
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-medium text-n-slate-12">Botões</span>
      <div ref="menuRoot" class="relative">
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-lg bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-3 disabled:opacity-50"
          :disabled="full"
          aria-haspopup="menu"
          :aria-expanded="menuOpen"
          @click="toggleMenu"
        >
          <span class="i-lucide-plus size-4" aria-hidden="true" />
          Adicionar botão
          <span class="i-lucide-chevron-down size-3.5" aria-hidden="true" />
        </button>
        <ul
          v-if="menuOpen"
          role="menu"
          class="absolute right-0 z-50 m-0 max-h-80 w-64 list-none overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-lg"
          :class="menuUp ? 'bottom-full mb-1' : 'top-full mt-1'"
        >
          <li v-for="item in BUTTON_TYPES" :key="item.value">
            <button
              type="button"
              role="menuitem"
              class="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!available(item.value)"
              @click="add(item.value)"
            >
              <span :class="item.icon" class="mt-0.5 size-4 shrink-0" />
              <span>
                <span class="block text-n-slate-12">{{ item.label }}</span>
                <span
                  v-if="item.limit < MAX_TEMPLATE_BUTTONS"
                  class="block text-xs text-n-slate-10"
                >
                  {{
                    item.limit === 1
                      ? '1 botão no máximo'
                      : `${item.limit} botões no máximo`
                  }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
    <p class="m-0 text-xs text-n-slate-10">
      Até {{ MAX_TEMPLATE_BUTTONS }} botões. A Meta pode agrupar os botões
      excedentes em "Ver todas as opções".
    </p>

    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="grid content-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
    >
      <div class="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
        <span
          class="flex size-6 items-center justify-center rounded-md bg-n-alpha-2 text-xs font-medium text-n-slate-11"
        >
          {{ index + 1 }}
        </span>
        <Select
          v-model="button.type"
          :options="optionsFor(button)"
          aria-label="Tipo do botão"
          class="!w-full [&>select]:w-full"
        />
        <button
          type="button"
          class="grid size-8 place-content-center rounded-lg text-n-ruby-11 hover:bg-n-ruby-3"
          aria-label="Remover botão"
          @click="remove(index)"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
      <input
        v-if="button.type !== 'COPY_CODE'"
        v-model="button.text"
        :maxlength="button.type === 'VOICE_CALL' ? 20 : 25"
        :class="INPUT"
        placeholder="Texto do botão"
      />
      <input
        v-if="button.type === 'URL'"
        v-model="button.url"
        :class="INPUT"
        placeholder="https://exemplo.com.br/{{1}}"
      />
      <input
        v-if="button.type === 'PHONE_NUMBER'"
        v-model="button.phone_number"
        maxlength="20"
        :class="INPUT"
        placeholder="+5511999999999"
      />
      <label
        v-if="button.type === 'VOICE_CALL'"
        class="grid gap-1 text-xs text-n-slate-11"
      >
        Botão ativo por (dias, de 1 a 30)
        <input
          v-model.number="button.ttl_days"
          type="number"
          min="1"
          max="30"
          :class="INPUT"
        />
        <span>
          A ligação é feita para o número desta caixa pelo WhatsApp; a chamada
          precisa estar ativada no número.
        </span>
      </label>
      <template v-if="button.type === 'FLOW'">
        <Select
          :model-value="button.flow_id"
          :options="
            flows.map(flow => ({
              value: String(flow.meta_flow_id),
              label: flow.name,
            }))
          "
          placeholder="Escolha o flow"
          aria-label="Flow"
          class="!w-full [&>select]:w-full"
          @update:model-value="selectFlow(button, $event)"
        />
        <p v-if="!flows.length" class="m-0 text-xs text-n-slate-10">
          Nenhum flow nesta caixa. Crie um em Configurações → WhatsApp Flows.
        </p>
      </template>
      <label
        v-if="button.type === 'COPY_CODE'"
        class="grid gap-1 text-xs text-n-slate-11"
      >
        Exemplo do código (até 15 caracteres). O código real é informado no
        envio.
        <input
          v-model="button.example"
          maxlength="15"
          :class="INPUT"
          placeholder="PROMO10"
        />
      </label>
    </div>
  </div>
</template>
